import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import styled from '@emotion/styled';
import { NumberType, NumberPosition, NumberCounterLayout } from '@teko-builder/types/pb';
import { getStyle } from './utils.es.js';
import { useState, useRef, useEffect } from 'react';
import { CircularProgressbarWithChildren } from 'react-circular-progressbar';
import { __rest } from 'tslib';

const getMaxValue = (value, total, type) => {
    let maxValue = total;
    if (type === NumberType.percent) {
        maxValue = 100;
    }
    return Math.max(value, maxValue);
};
const calculatePercent = (value, maxValue) => {
    return Math.min(100, (Math.round((value / maxValue) * 10000) / 10000) * 100);
};
const useFirstVisible = (element) => {
    const [isVisible, setState] = useState(false);
    const counter = useRef(0);
    useEffect(() => {
        if ((element === null || element === void 0 ? void 0 : element.current) && !counter.current) {
            const observer = new IntersectionObserver(([entry]) => {
                if (entry.isIntersecting && !counter.current) {
                    setState(entry.isIntersecting);
                    observer.unobserve(element.current);
                    counter.current++;
                }
            });
            observer.observe(element.current);
            return () => {
                if (element === null || element === void 0 ? void 0 : element.current) {
                    observer.unobserve(element.current);
                }
            };
        }
    }, [element]);
    return isVisible;
};

const BarProgress = ({ value, maxValue, numberElement, textBeforeElement, textAfterElement, style, }) => {
    const _a = style || {}, { numberPosition, duration = 0, delay = 0, foregroundColor } = _a, restStyle = __rest(_a, ["numberPosition", "duration", "delay", "foregroundColor"]);
    const barFillRef = useRef();
    useEffect(() => {
        let timer;
        if (value) {
            timer = setTimeout(async () => {
                if (barFillRef.current) {
                    barFillRef.current.style.removeProperty('transition');
                    barFillRef.current.style.width = `${calculatePercent(value, maxValue)}%`;
                }
            }, delay * 1000);
        }
        return () => {
            clearTimeout(timer);
            if (barFillRef.current) {
                barFillRef.current.style.transition = 'none';
                barFillRef.current.style.width = '0%';
            }
        };
    }, [value, maxValue]);
    return (jsxs(Fragment, { children: [textBeforeElement, numberPosition === NumberPosition.above && numberElement, jsx(BarContainer, Object.assign({ commonStyle: Object.assign({}, restStyle) }, { children: jsx(BarFill, Object.assign({ ref: barFillRef, commonStyle: { backgroundColor: foregroundColor }, duration: duration }, { children: numberPosition === NumberPosition.inside && numberElement })) })), numberPosition === NumberPosition.below && numberElement, textAfterElement] }));
};
const BarContainer = styled.div(({ commonStyle }) => `
  ${getStyle(commonStyle)}

  position: relative;
  width: 100%;
`);
const BarFill = styled.div(({ commonStyle, duration }) => {
    return `
      ${getStyle(commonStyle)}

      position: absolute;
      height: 100%;
      width: 0%;

      display: flex;
      align-items: center;

      overflow: hidden;
      transition: width ${duration}s linear;
    `;
});

const START_NUMBER = 0;
const NumberCounter = (props) => {
    const { number, total, numberType, layout, barStyle, circleStyle, duration = 0, delay = 0, prefixNumber = '', suffixNumber = '', textBefore, textAfter, foregroundColor, backgroundColor, } = props;
    const numberCounterRef = useRef();
    const isVisible = useFirstVisible(numberCounterRef);
    const positiveNumber = Math.abs(number || 0);
    const positiveTotal = Math.abs(total || 1);
    const maxValue = getMaxValue(positiveNumber, positiveTotal, numberType);
    const [progress, setProgress] = useState(START_NUMBER);
    useEffect(() => {
        if (number && isVisible) {
            let intervalId;
            const timer = setTimeout(async () => {
                const intervalTime = Math.floor((duration / positiveNumber) * 100) / 100;
                intervalId = setInterval(() => {
                    setProgress((preValue) => {
                        const newValue = preValue + 1;
                        if (newValue < positiveNumber)
                            return newValue;
                        clearInterval(intervalId);
                        clearTimeout(timer);
                        return positiveNumber;
                    });
                }, intervalTime * 1000);
            }, delay * 1000);
            return () => {
                clearInterval(intervalId);
                clearTimeout(timer);
                setProgress(START_NUMBER);
            };
        }
    }, [isVisible, layout, numberType, number, total, duration, delay]);
    const renderNumber = () => {
        const getNumber = () => {
            if (number === undefined)
                return;
            return number >= 0 ? progress : progress * -1;
        };
        const showedNumber = new Intl.NumberFormat('en-CA').format(getNumber());
        if (numberType === NumberType.standard) {
            return `${prefixNumber}${showedNumber}${suffixNumber}`;
        }
        return `${showedNumber}%`;
    };
    const numberElement = jsx("div", Object.assign({ className: "number" }, { children: renderNumber() }));
    const textBeforeElement = textBefore && (jsx("div", Object.assign({ className: "text" }, { children: textBefore })));
    const textAfterElement = textAfter && jsx("div", Object.assign({ className: "text" }, { children: textAfter }));
    const renderBarLayout = () => {
        const value = numberType === NumberType.percent && number < 0 ? 0 : positiveNumber;
        return (jsx(BarProgress, { value: value, maxValue: maxValue, numberElement: numberElement, textBeforeElement: textBeforeElement, textAfterElement: textAfterElement, style: Object.assign(Object.assign({}, barStyle), { backgroundColor,
                foregroundColor,
                duration,
                delay }) }));
    };
    const renderCircleLayout = () => {
        const value = numberType === NumberType.percent && number < 0 ? 0 : progress;
        return (jsx(CircleContainer, Object.assign({ circleStyle: circleStyle }, { children: jsx(CircularProgressbarWithChildren, Object.assign({ value: value, maxValue: maxValue, strokeWidth: circleStyle === null || circleStyle === void 0 ? void 0 : circleStyle.strokeSize, styles: {
                    path: {
                        stroke: foregroundColor,
                        transitionDuration: `${duration / positiveNumber}s`,
                    },
                    trail: {
                        stroke: backgroundColor,
                    },
                } }, { children: jsxs(CircleContent, { children: [textBeforeElement, numberElement, textAfterElement] }) })) })));
    };
    const renderContent = () => {
        if (layout === NumberCounterLayout.bar) {
            return renderBarLayout();
        }
        if (layout === NumberCounterLayout.circle) {
            return renderCircleLayout();
        }
        return (jsxs(Fragment, { children: [textBeforeElement, numberElement, textAfterElement] }));
    };
    return (jsx(Wrapper, Object.assign({ ref: numberCounterRef }, props, { children: renderContent() })));
};
NumberCounter.displayName = 'NumberCounter';
const CircleContainer = styled.div(({ circleStyle }) => `
    width: ${circleStyle === null || circleStyle === void 0 ? void 0 : circleStyle.size}px;
    height: ${circleStyle === null || circleStyle === void 0 ? void 0 : circleStyle.size}px;
    max-width: 100%;
    max-height: 100%;

    margin: auto;
  `);
const CircleContent = styled.div `
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;
const Wrapper = styled.div((props) => {
    const { commonStyle, numberStyle, textStyle } = props;
    return `
    ${getStyle(commonStyle)};
    overflow: hidden;

    .text {
      ${getStyle(textStyle)};
    }

    .number {
      width: 100%;
      height: 100%;
      ${getStyle(numberStyle)};
    }
  `;
});

export { NumberCounter as N };
